"use client";

import { Bot, Check, Clock3, GitCompareArrows, Keyboard, ShieldCheck, Sparkles, UserRound, X } from "lucide-react";
import { useLang } from "@/src/i18n";

export type AuditEntry = {
  id: string;
  at: string;
  user: string;
  action: string;
  diff: string;
  takeOrder: boolean;
  provenance?: "AI-suggested → accepted" | "AI-dictated → confirmed" | "AI-dictated → staged" | "Heartbeat → staged" | "User-entered" | "Template → confirmed";
  userInput?: string;
  aiSuggestion?: string;
  selectedOutput?: string;
  evidence?: string;
  model?: string;
};

type AuditDrawerProps = {
  open: boolean;
  entries: AuditEntry[];
  onClose: () => void;
};

export function AuditDrawer({ open, entries, onClose }: AuditDrawerProps) {
  const { t } = useLang();
  if (!open) return null;

  return (
    <div className="drawer-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        className="audit-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="drawer-head">
          <div>
            <span className="drawer-kicker"><ShieldCheck size={14} /> {t("audit.kicker")}</span>
            <h2 id="audit-title">{t("audit.title")}</h2>
            <p>{t("audit.sub")}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close audit log"><X size={19} /></button>
        </div>

        <div className="audit-list">
          {entries.map((entry, index) => (
            <article className="audit-entry" key={entry.id}>
              <div className="audit-timeline">
                <span>{entries.length - index}</span>
              </div>
              <div className="audit-body">
                <div className="audit-meta">
                  <span><Clock3 size={13} /> {entry.at}</span>
                  <span><UserRound size={13} /> {entry.user}</span>
                  {entry.takeOrder && <span className="take-stamp">{t("audit.takeOrder")}</span>}
                </div>
                <h3>{entry.action}</h3>
                <p><GitCompareArrows size={13} /> {entry.diff}</p>
                {(entry.userInput || entry.aiSuggestion || entry.selectedOutput) && (
                  <div className="audit-ai-comparison">
                    {entry.userInput && (
                      <div className="audit-comparison-row input">
                        <span><Keyboard size={12} /> {t("audit.doctorInput")}</span>
                        <p>{entry.userInput}</p>
                      </div>
                    )}
                    {entry.aiSuggestion && (
                      <div className="audit-comparison-row suggestion">
                        <span><Sparkles size={12} /> {t("audit.aiSuggestion")}</span>
                        <p>{entry.aiSuggestion}</p>
                      </div>
                    )}
                    {entry.selectedOutput && (
                      <div className="audit-comparison-row selected">
                        <span><Check size={12} /> {t("audit.selected")}</span>
                        <p>{entry.selectedOutput}</p>
                      </div>
                    )}
                    {(entry.evidence || entry.model) && (
                      <div className="audit-agent-meta">
                        {entry.evidence && <span>{t("soap.evidence")}: {entry.evidence}</span>}
                        {entry.model && <span>{t("audit.agent")}: {entry.model}</span>}
                      </div>
                    )}
                  </div>
                )}
                {entry.provenance && (
                  <span className={`provenance ${entry.provenance.startsWith("AI") || entry.provenance.startsWith("Heartbeat") ? "ai" : ""}`}>
                    {(entry.provenance.startsWith("AI") || entry.provenance.startsWith("Heartbeat")) && <Bot size={12} />}
                    {t(`prov.${entry.provenance}`)}
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
      </aside>
    </div>
  );
}
